import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Dropzone } from '@/components/ui/dropzone'
import { FileIcon, Trash01Icon, UploadIcon } from '@/components/ui/icons'
import { formatBytes } from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface InviteeUploadProps {
	/** Currently selected CSV file (uploaded on submit via multipart import). */
	file: File | null
	onFileChange: (file: File | null) => void
}

/**
 * Single-file CSV picker for the Salesforce invitee export. The selected file is
 * held by the form and sent to `POST /events/{id}/import` on submit.
 */
export function InviteeUpload({ file, onFileChange }: InviteeUploadProps) {
	const { t } = useTranslation()

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			const next = acceptedFiles[0]
			if (next) {
				onFileChange(next)
			}
		},
		[onFileChange]
	)

	const dropzoneState = useDropzone({
		onDrop,
		accept: { 'text/csv': ['.csv'] },
		maxFiles: 1,
		multiple: false
	})

	if (file) {
		return (
			<div className='flex items-center gap-3 rounded-[12px] border border-grey-600 px-4 py-3'>
				<FileIcon className='shrink-0 text-neutral-500' size={20} />
				<div className='flex min-w-0 flex-1 flex-col'>
					<span className='truncate font-medium text-neutral-900 text-sm'>
						{file.name}
					</span>
					<span className='text-neutral-500 text-xs'>
						{formatBytes(file.size)}
					</span>
				</div>
				<button
					className='flex shrink-0 cursor-pointer items-center gap-1 text-neutral-500 text-sm hover:text-danger-500'
					onClick={() => onFileChange(null)}
					type='button'
				>
					<Trash01Icon size={16} />
					{t('marquee:invitee_remove')}
				</button>
			</div>
		)
	}

	return (
		<Dropzone
			className={cn(
				'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[12px] border border-grey-600 border-dashed px-4 py-8 text-center transition-colors',
				dropzoneState.isDragActive && 'border-neutral-900 bg-neutral-50'
			)}
			state={dropzoneState}
		>
			<UploadIcon className='text-neutral-500' size={24} />
			<p className='font-medium text-neutral-900 text-sm'>
				{t('marquee:invitee_dropzone_title')}
			</p>
			<p className='text-neutral-500 text-xs'>
				{t('marquee:invitee_dropzone_hint')}
			</p>
		</Dropzone>
	)
}
